import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, Dropdown, Tag } from 'antd'
import { CaretDownOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import LogoutIcon from '@/assets/logout.svg?react'
import { listMyOrgs } from '@/api/org/list'
import type { SpaceItem } from '@/types'
import { isMockSpaceId } from '@/lib/currentOrg'
import { pickCurrentSpaceId, usableOrgsToSpaces } from '@/lib/orgSpaces'
import { findSpaceById, partitionSpacesByType } from '@/lib/spaces'
import { useAuthStore } from '@/store/useAuthStore'
import { useRuntimeStore } from '@/store/useRuntimeStore'
import { useUserStore } from '@/store'

function SpaceAvatar({ space, size = 28 }: { space?: SpaceItem; size?: number }) {
  const letter = (space?.name?.[0] ?? '?').toUpperCase()
  if (space?.logo) {
    return (
      <img
        src={space.logo}
        alt=""
        width={size}
        height={size}
        className="hdr-space-avatar"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <Avatar size={size} className="hdr-space-avatar--fallback">
      {letter}
    </Avatar>
  )
}

const ROLE_LABEL = {
  owner: 'spaceSwitcher.role.owner',
  admin: 'spaceSwitcher.role.admin',
  member: 'spaceSwitcher.role.member',
} as const

function roleLabel(
  role: SpaceItem['role'],
  translate: (key: string) => string,
): string | null {
  if (!role) return null
  return translate(ROLE_LABEL[role])
}

function SpaceSection({
  title,
  spaces,
  currentId,
  onSelect,
  scrollable,
}: {
  title: string
  spaces: SpaceItem[]
  currentId: string | null
  onSelect: (id: string) => void
  scrollable?: boolean
}) {
  const { t } = useTranslation()
  if (spaces.length === 0) return null

  return (
    <li className={scrollable ? 'hdr-popover-section hdr-popover-section--scroll' : 'hdr-popover-section'}>
      <div className="hdr-popover-section-title">{title}</div>
      <ul className="hdr-popover-list hdr-popover-list--section">
        {spaces.map((space) => {
          const active = space.id === currentId
          const role = roleLabel(space.role, (key) =>
            t(key as 'spaceSwitcher.role.owner'),
          )
          return (
            <li key={space.id}>
              <button
                type="button"
                className={`hdr-popover-item hdr-popover-item--space${active ? ' hdr-popover-item--active' : ''}`}
                onClick={() => onSelect(space.id)}
              >
                <SpaceAvatar space={space} size={28} />
                <div className="hdr-popover-space__main">
                  <span className="hdr-popover-space__name">{space.name}</span>
                  {space.type === 'workspace' && role ? (
                    <Tag className="hdr-role-chip" bordered={false}>
                      {role}
                    </Tag>
                  ) : null}
                </div>
              </button>
            </li>
          )
        })}
      </ul>
    </li>
  )
}

/** 拉已加入空间。PC 不渲染顶栏，所以由布局调用，不能只放在切换器里。 */
export function useLoadMySpaces() {
  const { i18n } = useTranslation()
  const replaceSpaces = useUserStore((s) => s.replaceSpaces)
  const failSpaceLoad = useUserStore((s) => s.failSpaceLoad)

  useEffect(() => {
    let cancelled = false
    void listMyOrgs()
      .then((data) => {
        if (cancelled) return
        const next = usableOrgsToSpaces(data.list, i18n.language)
        const currentId = useUserStore.getState().space_id
        replaceSpaces(next, pickCurrentSpaceId(next, currentId))
      })
      .catch(() => {
        if (!cancelled) failSpaceLoad()
      })
    return () => {
      cancelled = true
    }
  }, [failSpaceLoad, i18n.language, replaceSpaces])
}

/** 顶栏切换空间：列表切换 + 底部登出。无建立/加入/管理。 */
function SpaceSwitcher() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const space_id = useUserStore((s) => s.space_id)
  const spaces = useUserStore((s) => s.spaces)
  const setSpaceId = useUserStore((s) => s.setSpaceId)
  const listed = spaces.filter((space) => !isMockSpaceId(space.id))
  const logout = useAuthStore((s) => s.logout)
  const isClient = useRuntimeStore((s) => s.isClient)
  const current = findSpaceById(listed, space_id)
  const { personal, workspace } = partitionSpacesByType(listed)

  const handleSelect = (id: string) => {
    setSpaceId(id)
    setOpen(false)
  }

  const handleLogout = () => {
    setOpen(false)
    logout()
    if (isClient) return
    navigate('/login', { replace: true })
  }

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => (
        <div className="hdr-popover hdr-popover--switcher">
          <ul className="hdr-popover-list">
            <SpaceSection
              title={t('spaceSwitcher.personal')}
              spaces={personal}
              currentId={space_id}
              onSelect={handleSelect}
            />
            {personal.length > 0 && workspace.length > 0 ? (
              <li className="hdr-popover-divider" aria-hidden />
            ) : null}
            <SpaceSection
              title={t('spaceSwitcher.workspace')}
              spaces={workspace}
              currentId={space_id}
              onSelect={handleSelect}
              scrollable
            />
            <li className="hdr-popover-divider" aria-hidden />
            <li>
              <button
                type="button"
                className="hdr-popover-item hdr-popover-item--danger"
                onClick={handleLogout}
              >
                <LogoutIcon width={28} height={28} aria-hidden />
                <span>{t('spaceSwitcher.logout')}</span>
              </button>
            </li>
          </ul>
        </div>
      )}
    >
      <button
        type="button"
        className="hdr-space-entry"
        aria-label={t('spaceSwitcher.label')}
      >
        <SpaceAvatar space={current} size={28} />
        <span className="hdr-space-entry__name">
          {current?.name ?? t('spaceSwitcher.placeholder')}
        </span>
        <CaretDownOutlined className="hdr-space-entry__caret" />
      </button>
    </Dropdown>
  )
}

export default SpaceSwitcher
